export default {
  matching: {
    training: {
      legend: "Enables the module & the display in the catalogue",
      domains: {
        delete: {
          title: "Delete this training domain",
        },
      },
    },
    group: {
      title: "Group",
      legend: "Opens or closes applications",
    },
    groups: {
      title: ({ plural }) => plural(["Group", "Groups"]),
      edit: {
        title: "Edit the group sheet",
      },
      view: {
        title: "View the group sheet",
      },
      sheet: {
        title: "Group sheet",
        title_with_name: "Group sheet — {name}",
        candidacy_opening_section: "Applications opening",
        objectives_section: "Objectives & hosting capacity",
        save_success: "Group sheet saved",
      },
      candidacy_status_upcoming: "Upcoming",
      candidacy_status_open: "Open",
      candidacy_status_finalized: "Finalized",
      candidacy_status_full: "Full",
      candidacy_status_full_waitlist: "Full — waiting list",
      candidacy_status_closed: "Closed",
    },
    prospects: {
      title: ({ plural }) => plural(["Prospect", "Prospects"]),
      view: {
        title: "View the record",
      },
      send_email: {
        title: "Send an email",
      },
      filters: {
        title: "Quick filters",
        active: "Active",
        passive: "Passive",
        unsubscribed: "Unsubscribed",
        candidates: "Candidates",
        stages_title: "Filter candidates by step",
        advanced: {
          title: "Search",
          search_label: "Search by name or email",
          results_announcement: ({ plural, named }) =>
            plural(["No results", `${named("count")} result`, `${named("count")} results`]),
        },
      },
    },
    unsubscribeStats: {
      title: "Unsubscribes",
      total: ({ plural, named }) => plural([`${named("count")} unsubscribe`, `${named("count")} unsubscribes`]),
      by_reason: "Breakdown by reason",
      evolution: "Evolution over time",
      reason: "Reason",
      count: "Count",
      from: "Start period (month)",
      to: "End period (month)",
    },
    settings: {
      success: "Changes saved. They are now active.",
    },
    config: {
      candidateWorkflow: {
        edit_title: "Edit the “{name}” tab",
        edit_success: "Tab saved",
        blocks: {
          edit_title: "Edit the “{name}” block",
          edit_success: "Block saved",
        },
        fields: {
          edit_title: "Edit the “{name}” field",
          edit_success: "Field saved",
          conditional: "Shown only when “{target}” is filled in",
        },
        steps: {
          intro: "Steps",
          edit_title: "Edit the “{name}” step",
          edit_success: "Step saved",
        },
      },
    },
    catalog: {
      title: ({ plural }) => plural(["Catalogue", "Catalogues"]),
      edit: {
        title: "Edit the catalogue sheet",
      },
      view: {
        title: "View the catalogue sheet",
      },
      sheet: {
        title: "Catalogue sheet",
        title_with_name: "Catalogue sheet — {name}",
        subtitle: "Elements visible on the catalogue and the landing page",
        mode_edit: "Edit mode",
        mode_view: "Read only — right not granted",
        presentation_image: "Presentation picture",
        presentation_text: "Presentation text",
        commercial_brochure: "Commercial brochure",
        website_url: "Website URL",
        no_file: "No file",
        qr_code_alt: "Catalogue sheet QR code",
        qr_url_label: "Link encoded by the QR code",
        save_success: "Catalogue sheet saved",
        download_unavailable: "No brochure available for download.",
        actions: {
          replace: "Replace",
          remove: "Remove",
          download: "Download",
          copy: "Copy image",
          copy_success: "QR code copied to clipboard!",
          copy_error: "Unable to copy the QR code.",
          copy_unsupported: "Image copying is not supported by your browser.",
          copy_url: "Copy link",
          copy_url_success: "Link copied to clipboard!",
          copy_url_error: "Unable to copy the link.",
          uploading: "Uploading file…",
          upload_error: "An error occurred while uploading the file.",
        },
      },
    },
    landing_page: {
      add: {
        title: "Add a source",
        success: "Source successfully added!",
      },
      edit: {
        title: "Edit this source",
        success: "Source successfully updated!",
      },
      delete: {
        title: "Delete this source",
        confirmation: "Are you sure you want to delete this landing page source?",
        success: "Landing page source successfully deleted!",
      },
      qr_code: {
        see: "View QR code",
        title: "Landing page QR code",
        alt: "Landing page QR code",
        download: "Download",
        copy: "Copy image",
        copy_success: "QR code copied to clipboard!",
        copy_error: "Unable to copy the QR code.",
        copy_unsupported: "Image copying is not supported by your browser.",
      },
      url: {
        copy: {
          title: "Copy link",
          success: "Link successfully copied!",
        },
      },
      filters: {
        level: {
          label: "Target level",
          unknown: "I don't know",
          load_error: "Unable to load levels, please try again.",
          retry: "Retry",
        },
        domain: {
          label: "Training domain",
          hint_multiple: "(multiple choices possible)",
          search_placeholder: "Search a domain (e.g. IT, sales…)",
          empty_search: "No domain matches “{search}”.",
          select_level_first: "Choose a target level to see the available domains.",
          show_all: "Show the {count} domains",
          collapse: "Collapse domains",
          load_error: "Unable to load domains, please try again.",
          retry: "Retry",
          remove: "Remove {name}",
          formation_count: ({ plural, named }) =>
            plural([`${named("count")} training course`, `${named("count")} training courses`]),
        },
        training: {
          label: "Available training courses",
          search_placeholder: "Search a training course by keyword…",
          selected_count: ({ plural, named }) =>
            plural([
              `${named("count")}/${named("max")} training course selected`,
              `${named("count")}/${named("max")} training courses selected`,
            ]),
          // No cap (attr["data-max-count"] absent) : no "/{max}" to show.
          selected_count_no_max: ({ plural, named }) =>
            plural([`${named("count")} training course selected`, `${named("count")} training courses selected`]),
          total_count: ({ plural, named }) => plural([`${named("total")} result`, `${named("total")} results`]),
          load_more: ({ plural, named }) =>
            plural([
              `See ${named("count")} more training course (${named("remaining")} remaining)`,
              `See ${named("count")} more training courses (${named("remaining")} remaining)`,
            ]),
          adapted_to_level: "Suited to your level",
          empty: "No training course matches these criteria for now.",
          more_info: "More info",
          close: "Close",
          select: "Select this training course",
          selected: "Training course selected",
          max_selection_reached: "You can only select up to {max} training courses.",
          load_error: "Unable to load training courses, please try again.",
          retry: "Retry",
        },
      },
    },
    candidateStudies: {
      title: "My studies",
      intro: "Fill in your degrees and certifications to complete your application.",
      add: "Add",
      add_degree_title: "Add a degree",
      edit_degree_title: "Edit this degree",
      add_certification_title: "Add a certification",
      edit_certification_title: "Edit this certification",
      save_success: "Entry saved",
      empty: "No entry yet",
      minimum_not_reached: "Add at least one entry to complete this block",
      max_reached_tooltip: "Maximum number of entries reached",
      delete_confirm_title: "Delete this entry?",
      delete_success: "Entry deleted",
      move_up: "Move up",
      move_down: "Move down",
      moved: "{name}: entry number {position} of {total}.",
      reorder_error: "The order of your entries could not be saved, the previous order has been kept.",
      entry: {
        edit: "Edit",
        delete: "Delete",
        degree_type: "Degree type",
        institution: "School",
        department: "Department",
        country: "Country",
        year: "Year",
        organization: "Organization",
      },
    },
    candidateInformation: {
      read_only_notice: "This tab can no longer be edited at this stage of your file.",
      steps_title: "My file",
      title: "My information",
      tab_empty: "This tab will be available soon.",
      load_error: "Unable to load your file, please try again.",
      retry: "Retry",
    },
    applications: {
      hint_reorder:
        "Rank your choices in order of preference: drag a card by its handle or use the Move up and Move down buttons.",
      empty: "You do not have any application choice yet.",
      rank: ({ named }) => {
        const rank = named("rank");
        const suffixes = { 1: "st", 2: "nd", 3: "rd" };
        const suffix = rank % 100 >= 11 && rank % 100 <= 13 ? "th" : (suffixes[rank % 10] ?? "th");
        return `${rank}${suffix} choice`;
      },
      move_up: "Move choice {name} up",
      move_down: "Move choice {name} down",
      moved: "{name}: choice number {position} of {total}.",
      reorder_error: "The order of your choices could not be saved, the previous order has been kept.",
      closed_title: "Closed choices",
      view: "View",
      edit: "Edit",
      tab_count: "{count} active choice | {count} active choices",
      modal: {
        title: "My choice",
        training: "Training",
        group: "Group",
        status: "Status",
        year: "Year",
        letter_label: "Motivation letter",
        letter_hint: "{max} characters maximum.",
        letter_counter: "{count} / {max} characters",
        letter_locked: "Your motivation letter can no longer be edited at this stage.",
        save: "Save",
        close: "Close",
        saved: "Motivation letter saved",
        withdraw: "Withdraw this choice",
        withdraw_confirm: "Do you really want to withdraw this choice?",
        withdraw_confirm_yes: "Yes, withdraw this choice",
        withdraw_cancel: "Cancel",
        withdrawn: "Choice withdrawn",
      },
    },
  },
};
